import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCan } from "@/lib/perms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Factory, AlertTriangle, Send, Pencil, Check, Clock, Siren } from "lucide-react";
import { EmergencyOrderDialog } from "@/components/EmergencyOrderDialog";

const ymd = (d) => d.toISOString().slice(0, 10);
function rangeFor(key, custom, today) {
  const t = today ? new Date(`${today}T00:00:00Z`) : new Date(`${new Date().toLocaleDateString("en-CA")}T00:00:00Z`);
  if (key === "today") return [ymd(t), ymd(t)];
  if (key === "yesterday") { const y = new Date(t); y.setUTCDate(t.getUTCDate() - 1); return [ymd(y), ymd(y)]; }
  if (key === "week") { const s = new Date(t); s.setUTCDate(t.getUTCDate() - ((t.getUTCDay() + 6) % 7)); return [ymd(s), ymd(t)]; }
  return [custom.start || ymd(t), custom.end || custom.start || ymd(t)];
}
const fmt = (n) => Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export function StockQtyEditor({ productId, value, unit, onSaved, testid }) {
  const can = useCan();
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(value ?? "");
  useEffect(() => { setV(value ?? ""); }, [value]);
  if (!productId) return <span className="text-xs text-stone-400">Not in catalogue</span>;
  const save = async () => {
    try { await api.post(`/products/${productId}/stock-qty`, { stock_qty: v === "" ? null : Number(v) }); toast.success("Stock updated"); setEdit(false); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  if (!edit) {
    return (
      <span className="inline-flex items-center gap-1">
        <span data-testid={`${testid}-value`}>{value == null ? "Not set" : `${fmt(value)} ${unit}`}</span>
        {can("catalog.edit", "inventory.edit") && <button type="button" onClick={() => setEdit(true)} className="text-stone-400 hover:text-[#0D5C3A]" data-testid={`${testid}-edit`}><Pencil className="h-3.5 w-3.5" /></button>}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <Input type="number" min="0" step="any" value={v} onChange={(e) => setV(e.target.value)} className="h-8 w-24" data-testid={`${testid}-input`} />
      <Button size="sm" variant="outline" className="h-8 px-2" onClick={save} data-testid={`${testid}-save`}><Check className="h-4 w-4" /></Button>
    </span>
  );
}

export function OrderWindowStatus({ win }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(t); }, []);
  if (!win?.enabled) return null;
  const mins = Math.max(0, Math.round((new Date(win.next_change).getTime() - now) / 60000));
  const left = mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
  return (
    <span data-testid="order-window-status" className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${win.is_open ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
      <Clock className="h-3.5 w-3.5" /> {win.is_open ? `Partner ordering open · closes in ${left}` : `Partner ordering closed · reopens in ${left}`}
    </span>
  );
}

function SendDialog({ open, onOpenChange, start, end }) {
  const [people, setPeople] = useState([]);
  const [sel, setSel] = useState([]);
  const [note, setNote] = useState("");
  useEffect(() => { if (open) { api.get("/org/people").then((r) => setPeople(r.data.people)); setSel([]); setNote(""); } }, [open]);
  const send = async () => {
    try { const r = await api.post("/orders/demand/send", { user_ids: sel, start, end, note }); toast.success(`Sent to ${r.data.sent_to} team member(s)`); onOpenChange(false); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-1.5rem)] max-w-md overflow-y-auto" data-testid="demand-send-dialog">
        <DialogHeader><DialogTitle>Send to production team</DialogTitle></DialogHeader>
        <div className="max-h-64 space-y-2 overflow-y-auto">
          {people.map((p) => (
            <label key={p.id} className="flex cursor-pointer items-center gap-2 text-sm">
              <Checkbox checked={sel.includes(p.id)} onCheckedChange={() => setSel(sel.includes(p.id) ? sel.filter((x) => x !== p.id) : [...sel, p.id])} data-testid={`demand-send-person-${p.id}`} />
              {p.name} <span className="text-xs text-stone-400">{p.role === "owner" ? "Super Admin" : p.job_role}</span>
            </label>
          ))}
        </div>
        <Input placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="demand-send-note" />
        <DialogFooter><Button disabled={!sel.length} onClick={send} className="bg-[#0D5C3A] hover:bg-[#084229]" data-testid="demand-send-confirm"><Send className="mr-1 h-4 w-4" /> Send</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProductOrdersDialog({ row, onClose }) {
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-1.5rem)] max-w-lg overflow-y-auto" data-testid="demand-product-dialog">
        <DialogHeader><DialogTitle>{row?.product} · {row && fmt(row.total)} {row?.unit}</DialogTitle></DialogHeader>
        <div className="divide-y divide-[#F0ECE1]">
          {row?.orders.map((o) => (
            <div key={o.id + o.quantity} className="flex items-center justify-between py-2 text-sm" data-testid={`demand-order-${o.order_code}`}>
              <div><p className="font-semibold text-stone-800">{o.partner}</p><p className="font-mono text-xs text-stone-400">{o.order_code} · {o.status}{o.emergency ? " · Emergency" : ""}</p></div>
              <span className="font-semibold">{fmt(o.quantity)} {row.unit}</span>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const RANGES = [["today", "Today"], ["yesterday", "Yesterday"], ["week", "This Week"], ["custom", "Custom"]];

export function DemandSummary() {
  const can = useCan();
  const [key, setKey] = useState("today");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [row, setRow] = useState(null);
  const [send, setSend] = useState(false);
  const [emerg, setEmerg] = useState(false);
  const [today, setToday] = useState(null);
  const [start, end] = rangeFor(key, custom, today);
  const load = () => api.get("/orders/demand", { params: { start, end } }).then((r) => { setData(r.data); if (r.data.today && r.data.today !== today) setToday(r.data.today); }).catch(() => setData({ products: [] }));
  useEffect(() => { load(); }, [start, end]); // eslint-disable-line react-hooks/exhaustive-deps
  const short = (data?.products || []).filter((p) => p.shortage > 0).length;
  return (
    <div className="rounded-2xl border border-[#E5E0D8] bg-white p-5 shadow-sm" data-testid="demand-summary">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-stone-900"><Factory className="h-5 w-5 text-[#0D5C3A]" /> Product demand</h2>
          <p className="text-sm text-stone-500">Total quantity ordered across all incoming orders (declined & missed excluded).</p>
          <div className="mt-2"><OrderWindowStatus win={data?.order_window} /></div>
        </div>
        <div className="flex flex-wrap gap-2">
          {can("orders.create") && <Button size="sm" variant="outline" onClick={() => setEmerg(true)} data-testid="emergency-order-btn"><Siren className="mr-1 h-4 w-4 text-[#C85A32]" /> Emergency Order</Button>}
          <Button size="sm" disabled={!data?.products.length} onClick={() => setSend(true)} className="bg-[#0D5C3A] hover:bg-[#084229]" data-testid="demand-send-btn"><Send className="mr-1 h-4 w-4" /> Send to production team</Button>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {RANGES.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setKey(k)} data-testid={`demand-range-${k}`}
            className={`rounded-full border px-3 py-1 text-xs font-semibold ${key === k ? "border-[#0D5C3A] bg-[#0D5C3A] text-white" : "border-[#E5E0D8] text-stone-600 hover:border-[#84A98C]"}`}>{l}</button>
        ))}
        {key === "custom" && <>
          <Input type="date" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })} className="h-8 w-40" data-testid="demand-start" />
          <Input type="date" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })} className="h-8 w-40" data-testid="demand-end" />
        </>}
      </div>
      {short > 0 && <p className="mt-3 flex items-center gap-2 text-sm font-medium text-rose-600" data-testid="demand-shortage-alert"><AlertTriangle className="h-4 w-4" /> {short} product{short > 1 ? "s" : ""} short on stock</p>}
      {!data ? null : data.products.length === 0 ? <p className="mt-4 text-sm text-stone-500" data-testid="demand-empty">No products ordered in this period.</p> : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm sm:min-w-[560px]">
            <thead className="hidden text-left text-xs uppercase tracking-wider text-stone-400 sm:table-header-group"><tr><th className="py-2">Product</th><th>Total ordered</th><th>Partners</th><th>In stock</th><th>Shortage</th></tr></thead>
            <tbody className="divide-y divide-[#F0ECE1]">
              {data.products.map((p) => (
                <tr key={p.product + p.unit} className={`flex flex-wrap gap-x-4 gap-y-1 py-2 sm:table-row ${p.shortage > 0 ? "bg-rose-50/60" : ""}`} data-testid={`demand-row-${p.product.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <td className="w-full sm:w-auto sm:py-2.5"><button type="button" onClick={() => setRow(p)} className="font-semibold text-[#0D5C3A] hover:underline" data-testid={`demand-open-${p.product.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{p.product}</button></td>
                  <td className="font-semibold">{fmt(p.total)} {p.unit}{p.pending !== p.total && <span className="block text-xs font-normal text-stone-500" data-testid={`demand-pending-${p.product.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{fmt(p.pending)} still to deliver</span>}</td>
                  <td><span className="sm:hidden text-xs text-stone-400">Partners: </span>{p.partner_count}</td>
                  <td><span className="sm:hidden text-xs text-stone-400">Stock: </span><StockQtyEditor productId={p.product_id} value={p.stock_qty} unit={p.unit} onSaved={load} testid={`demand-stock-${p.product.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} /></td>
                  <td>{p.shortage == null ? <span className="text-xs text-stone-400">Set stock to check</span> : p.shortage > 0 ? <span className="font-semibold text-rose-600">{fmt(p.shortage)} {p.unit}</span> : <span className="text-emerald-700">Enough</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ProductOrdersDialog row={row} onClose={() => setRow(null)} />
      <SendDialog open={send} onOpenChange={setSend} start={start} end={end} />
      <EmergencyOrderDialog open={emerg} onOpenChange={setEmerg} onSaved={load} />
    </div>
  );
}
